/**
 * Error model. Every rejected call from the factory instance throws the canonical
 * `ApiError { code, status, requestId, details }` — callers `switch (err.code)` and
 * never parse raw responses.
 *
 * `ApiError` itself is re-exported from the vendored factory so there is exactly ONE
 * class in the process and `instanceof` holds across modules. The union below is the
 * repo's catalogue of backend codes (from the two handoff contracts) — the factory
 * passes `error.code` through verbatim, so this is a documentation + autocomplete
 * surface rather than a filter; an unrecognized code still arrives as-is.
 */
import { ApiError } from './apiAxiosInstance';

export { ApiError };

export type BrandCenterErrorCode =
  // Session / CSRF / transport (factory-synthesized or saas-auth)
  | 'SESSION_EXPIRED'
  | 'CSRF_VALIDATION_FAILED'
  | 'INVALID_CSRF'
  | 'INVALID_ORIGIN'
  | 'REFRESH_ORIGIN_UNROUTABLE'
  | 'NETWORK_ERROR'
  | 'RATE_LIMIT_REJECTED'
  | 'UNKNOWN'
  // Centralized-login kickoff / return (genesis handoff §2, §3)
  | 'INVALID_RETURN_URL'
  | 'TENANT_CONTEXT_REQUIRED'
  | 'OAUTH_FAILED'
  | 'INVALID_OAUTH_STATE'
  | 'PROVIDER_NOT_ALLOWED'
  | 'OAUTH_TENANT_MISMATCH'
  // Tenant-scoped brand API (api-contract handoff §1)
  | 'OWNED_BRAND_NOT_FOUND'
  | 'BRAND_NOT_CONFIGURED'
  | 'TENANT_HAS_NO_PRODUCT'
  | 'INVALID_TEMPLATE'
  | 'INVALID_AUTH_PROVIDER'
  // Tenant-scoped email template editor (SASBND-0005 / SASATH-0204/0205)
  | 'UNKNOWN_TOKEN'
  | 'TOKEN_NOT_ALLOWED_IN_SUBJECT'
  | 'TOKEN_NOT_ALLOWED_IN_BODY'
  | 'SUBJECT_TOO_LONG'
  | 'BODY_TOO_LONG'
  | 'SUBJECT_REQUIRED'
  | 'BODY_REQUIRED'
  | 'UNAUTHORIZED'
  | 'NO_DRAFT_TO_PUBLISH'
  | 'TEST_SEND_RATE_LIMITED'
  // Tenant-scoped opaque siteConfig store (SASBND-0007 / SASATH-0207)
  | 'SITE_CONFIG_TOO_LARGE'
  // Brand asset uploads (api-contract handoff §1, §5)
  | 'INVALID_IMAGE'
  | 'UNSUPPORTED_IMAGE_TYPE'
  | 'IMAGE_TOO_LARGE'
  | 'IMAGE_DIMENSIONS_TOO_LARGE';

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

/** The backend code of a thrown error, or `null` when it isn't an ApiError. */
export function errorCodeOf(err: unknown): string | null {
  return isApiError(err) ? err.code : null;
}
